// ═══════════════════════════════════════════════════════════════════════════════
// Task List Page (Employer) — date strip + time-of-day grouped instances
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { scrollMainAppAnchorIntoView } from '../../lib/mainAppScroll';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import { EmployerTaskDayRow } from '../../components/task/employer/EmployerTaskDayRow';
import { EmployerOverdueTasksCard } from '../../components/task/employer/EmployerOverdueTasksCard';
import { EmployerTimeBucketIcon } from '../../components/task/employer/EmployerTimeBucketIcon';
import { taskInstanceApi } from '../../lib/api';
import {
  addCalendarDaysToYmd,
  buildEmployerDateStripDays,
  formatEmployerDateStripDayNumber,
  formatEmployerDateStripWeekday,
  formatEmployerSelectedDateHeading,
  getDateInTimezone,
  EMPLOYER_TIME_BUCKET_I18N,
  groupEmployerDayTasksByTimeBucket,
  isIsoYmd,
} from '../../utils/employerTaskDayGrouping';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { useEmployerEditTaskNavigation } from '../../hooks/useEmployerEditTaskNavigation';
import { useEmployerTaskDateStripPin } from '../../hooks/useEmployerTaskDateStripPin';
import { useTotalTaskCreationGate } from '../../hooks/useTotalTaskCreationGate';
import { AppButton } from '../../components/ui/AppButton';
import { AppModal } from '../../components/ui/AppModal';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { EmployerTaskListTitleIcon } from '../../components/task/employer/EmployerTaskListTitleIcon';
import { TaskListLegacyRedirect } from './TaskListLegacyRedirect';
import { APPROVAL_STATUS, intlLocaleForLanguage, USER_LEVEL, type EmployerTodayTaskItem } from '@fdh/shared';
import { emitTaskKindNotAllowed } from '../../lib/limitError';

type DateStripSelection =
  | { mode: 'strip'; dateYmd: string }
  | { mode: 'custom'; dateYmd: string };

/** Scroll main app shell to the date strip and center tomorrow in the horizontal strip. */
const scrollEmployerTaskDateStripIntoView = (tomorrowYmd: string): void => {
  const anchor = document.getElementById('employer-task-date-strip-anchor');
  if (anchor) {
    anchor.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
  }

  const stripEl = document.getElementById('employer-task-date-strip-scroll');
  const tomorrowBtn = stripEl?.querySelector(`[data-date="${tomorrowYmd}"]`);
  if (tomorrowBtn instanceof HTMLElement) {
    tomorrowBtn.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
  }

  if (anchor) {
    scrollMainAppAnchorIntoView(anchor, 12);
  }
};

const scheduleScrollToTomorrowDateStrip = (tomorrowYmd: string): void => {
  const run = () => scrollEmployerTaskDateStripIntoView(tomorrowYmd);
  run();
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(run);
  });
  window.setTimeout(run, 96);
  window.setTimeout(run, 320);
};

type EmployerTaskSummaryTileProps = {
  ariaLabel: string;
  label: string;
  count: number;
  countUnitLabel: string;
  iconWrapClassName: string;
  icon: ReactNode;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
};

const TaskKindLockIcon = ({ className }: { className?: string }) => (
  <svg
    className={className ?? 'h-5 w-5 shrink-0'}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

const EmployerTaskSummaryTile = ({
  ariaLabel,
  label,
  count,
  countUnitLabel,
  iconWrapClassName,
  icon,
  onClick,
  onKeyDown,
}: EmployerTaskSummaryTileProps) => (
  <button
    type="button"
    onClick={onClick}
    onKeyDown={onKeyDown}
    tabIndex={0}
    className="flex h-full min-w-0 flex-col rounded-2xl border border-gray-300/80 bg-white p-4 text-left shadow-sm transition-colors hover:border-primary-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
    aria-label={ariaLabel}
  >
    <div className="mb-2 flex min-h-[2.75rem] shrink-0 items-start justify-between gap-2">
      <span className="min-w-0 flex-1 text-xs font-semibold leading-snug text-gray-600 dark:text-gray-300">
        {label}
      </span>
      <svg className="h-3.5 w-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
    <div className="flex items-center gap-2">
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${iconWrapClassName}`}>
        {icon}
      </div>
      <span className="text-2xl font-black tabular-nums text-gray-900 dark:text-gray-100">{count}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{countUnitLabel}</span>
    </div>
  </button>
);

export const TaskListPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleEditTaskInstanceTemplate } = useEmployerEditTaskNavigation();
  const { guardOpenCreateTaskChoice, guardNavigateToCreateTask } = useTotalTaskCreationGate();
  const isHighTierTaskKindLocked = user?.level === USER_LEVEL.FREE;

  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const todayYmd = useMemo(() => getDateInTimezone(timezone), [timezone]);
  const tomorrowYmd = useMemo(() => addCalendarDaysToYmd(todayYmd, 1), [todayYmd]);
  const maxCustomDateYmd = useMemo(() => addCalendarDaysToYmd(todayYmd, 7), [todayYmd]);
  const stripDays = useMemo(() => buildEmployerDateStripDays(todayYmd), [todayYmd]);

  const dateParam = searchParams.get('date');
  const tabParam = searchParams.get('tab');

  const [selection, setSelection] = useState<DateStripSelection>(() => {
    if (isIsoYmd(dateParam)) {
      if (stripDays.includes(dateParam)) {
        return { mode: 'strip', dateYmd: dateParam };
      }
      return { mode: 'custom', dateYmd: dateParam };
    }
    return { mode: 'strip', dateYmd: todayYmd };
  });

  const [todayTasks, setTodayTasks] = useState<EmployerTodayTaskItem[]>([]);
  const [dayTasks, setDayTasks] = useState<EmployerTodayTaskItem[]>([]);
  const [pendingReviewTotal, setPendingReviewTotal] = useState(0);
  const [tomorrowTaskCount, setTomorrowTaskCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dayError, setDayError] = useState<string | null>(null);
  const [addTaskChoiceOpen, setAddTaskChoiceOpen] = useState(false);
  const [customDateModalOpen, setCustomDateModalOpen] = useState(false);
  const [customDraftDate, setCustomDraftDate] = useState(todayYmd);
  const [titleMenuOpen, setTitleMenuOpen] = useState(false);
  const titleMenuRef = useRef<HTMLDivElement>(null);
  const dateStripAnchorRef = useRef<HTMLDivElement>(null);
  const dateStripPanelRef = useRef<HTMLDivElement>(null);
  const { isPinned: isDateStripPinned, pinnedTop, spacerHeightPx } = useEmployerTaskDateStripPin(
    dateStripAnchorRef,
    dateStripPanelRef,
  );

  const selectedDateYmd = selection.dateYmd;
  const isSelectedDateOnStrip = stripDays.includes(selectedDateYmd);
  const isCustomSlotActive = !isSelectedDateOnStrip;

  const employerTodayCurrentMinutes = useMemo(() => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
    const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
    return hour * 60 + minute;
  }, [timezone]);

  useEffect(() => {
    if (!isIsoYmd(dateParam)) {
      return;
    }
    if (stripDays.includes(dateParam)) {
      setSelection({ mode: 'strip', dateYmd: dateParam });
      return;
    }
    setSelection({ mode: 'custom', dateYmd: dateParam });
  }, [dateParam, stripDays]);

  useEffect(() => {
    if (!isSelectedDateOnStrip || selection.mode === 'strip') {
      return;
    }
    setSelection({ mode: 'strip', dateYmd: selectedDateYmd });
  }, [isSelectedDateOnStrip, selectedDateYmd, selection.mode]);

  useEffect(() => {
    if (tabParam) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (next.get('date') !== selectedDateYmd) {
      next.set('date', selectedDateYmd);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, selectedDateYmd, setSearchParams, tabParam]);

  useEffect(() => {
    if (!titleMenuOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!titleMenuRef.current?.contains(event.target as Node)) {
        setTitleMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [titleMenuOpen]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [todayResponse, overdueResponse, pendingRes, tomorrowRes] = await Promise.all([
        taskInstanceApi.employerTodayList(),
        taskInstanceApi.overdueList(),
        taskInstanceApi.pendingReview(),
        taskInstanceApi.employerFuture(tomorrowYmd, tomorrowYmd),
      ]);

      if (!todayResponse.success) {
        setError(todayResponse.error ?? t('task.list.loadError'));
        return;
      }
      if (!overdueResponse.success) {
        setError(overdueResponse.error ?? t('task.list.loadError'));
        return;
      }

      setTodayTasks(todayResponse.data?.tasks ?? []);
      setOverdueCount(overdueResponse.data?.tasks?.length ?? 0);

      if (pendingRes.success && pendingRes.data) {
        setPendingReviewTotal(pendingRes.data.total ?? pendingRes.data.tasks.length);
      } else {
        setPendingReviewTotal(0);
      }
      if (tomorrowRes.success && tomorrowRes.data) {
        setTomorrowTaskCount(tomorrowRes.data.total ?? tomorrowRes.data.tasks.length);
      } else {
        setTomorrowTaskCount(0);
      }
    } catch {
      setError(t('task.list.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, tomorrowYmd]);

  const fetchDayTasks = useCallback(async (dateYmd: string) => {
    setDayLoading(true);
    setDayError(null);
    try {
      const response = await taskInstanceApi.employerByDate(dateYmd);
      if (!response.success) {
        setDayError(response.error ?? t('task.list.loadError'));
        return;
      }
      setDayTasks(response.data?.tasks ?? []);
    } catch {
      setDayError(t('task.list.loadError'));
    } finally {
      setDayLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    void fetchDayTasks(selectedDateYmd);
  }, [fetchDayTasks, selectedDateYmd]);

  const taskListTodayCompletion = useMemo(() => {
    const totalToday = todayTasks.length;
    const completedToday = todayTasks.filter((row) => row.status === APPROVAL_STATUS.APPROVED).length;
    const pct =
      totalToday === 0 ? 0 : Math.round((completedToday / Math.max(totalToday, 1)) * 100);
    return { totalToday, completedToday, pct };
  }, [todayTasks]);

  const taskListTodayLabel = useMemo(() => {
    const now = new Date();
    const locale = intlLocaleForLanguage(language);
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      month: 'long',
      day: 'numeric',
    }).format(now);
  }, [language, timezone]);

  const groupedDayTasks = useMemo(
    () => groupEmployerDayTasksByTimeBucket(dayTasks),
    [dayTasks],
  );

  const summaryCountUnitLabel = (n: number) =>
    t(n === 1 ? 'task.list.summaryCountUnitOne' : 'task.list.summaryCountUnitOther');

  const applyDateSelection = (nextSelection: DateStripSelection) => {
    setSelection(nextSelection);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    next.set('date', nextSelection.dateYmd);
    setSearchParams(next, { replace: true });
  };

  const handleStripDateSelect = (dateYmd: string) => {
    applyDateSelection({ mode: 'strip', dateYmd });
  };

  const handleOpenCustomDateModal = () => {
    setCustomDraftDate(isCustomSlotActive ? selectedDateYmd : todayYmd);
    setCustomDateModalOpen(true);
  };

  const handleConfirmCustomDate = () => {
    const onStrip = stripDays.includes(customDraftDate);
    applyDateSelection({
      mode: onStrip ? 'strip' : 'custom',
      dateYmd: customDraftDate,
    });
    setCustomDateModalOpen(false);
  };

  const handlePendingReviewClick = () => {
    navigate('/tasks/pending-review');
  };

  const handleTomorrowCardClick = () => {
    applyDateSelection({ mode: 'strip', dateYmd: tomorrowYmd });
    scheduleScrollToTomorrowDateStrip(tomorrowYmd);
  };

  const handleAddTaskFabClick = () => {
    void guardOpenCreateTaskChoice(() => setAddTaskChoiceOpen(true));
  };

  const handleCreateTaskChoice = (path: string, isHighTierTaskKind: boolean) => {
    if (isHighTierTaskKind && isHighTierTaskKindLocked) {
      emitTaskKindNotAllowed();
      return;
    }
    void guardNavigateToCreateTask(navigate, path, () => setAddTaskChoiceOpen(false));
  };

  const handleOpenDetail = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}`);
  };

  const handleReview = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}/review`);
  };

  if (tabParam) {
    return <TaskListLegacyRedirect />;
  }

  return (
    <div className="min-h-full bg-[#f7f8fb] px-4 py-6 dark:bg-gray-950">
      <AppPageTitleSection
        title={t('task.list.title')}
        subtitle={t('task.list.pageSubtitle')}
        endDecoration={
          <EmployerTaskListTitleIcon className="pointer-events-none absolute bottom-[5px] right-[calc(2.5rem-5px)] z-0 h-[6.93rem] w-[6.93rem] shrink-0" />
        }
      >
        <div className="relative z-10 shrink-0" ref={titleMenuRef}>
            <button
              type="button"
              onClick={() => setTitleMenuOpen((prev) => !prev)}
              tabIndex={0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200/80 bg-white text-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
              aria-label={t('task.list.titleMenuAria')}
              aria-expanded={titleMenuOpen}
              aria-haspopup="menu"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3}>
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </g>
              </svg>
            </button>
            {titleMenuOpen ? (
              <div
                className="absolute right-0 top-9 z-50 w-44 overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={0}
                  className="flex w-full items-center gap-2 border-b border-gray-200/40 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
                  onClick={() => {
                    setTitleMenuOpen(false);
                    navigate('/tasks/edit-all');
                  }}
                >
                  {t('task.list.menuEditAllTasks')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={0}
                  className="flex w-full items-center gap-2 border-b border-gray-200/40 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
                  onClick={() => {
                    setTitleMenuOpen(false);
                    navigate('/tasks/history');
                  }}
                >
                  {t('task.list.menuHistoryTasks')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={0}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] font-medium text-gray-900 hover:bg-gray-50 focus:outline-none dark:text-gray-100 dark:hover:bg-gray-800"
                  onClick={() => {
                    setTitleMenuOpen(false);
                    navigate('/tasks/future');
                  }}
                >
                  {t('task.list.menuFutureTasks')}
                </button>
              </div>
            ) : null}
        </div>
      </AppPageTitleSection>

      <section
        className="mb-3 rounded-2xl border border-gray-300/80 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
        aria-labelledby="task-list-today-completion-heading"
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <h2 id="task-list-today-completion-heading" className="text-[13px] font-semibold text-gray-600 dark:text-gray-300">
            {t('employerDashboard.todayCompletion')}
          </h2>
          <span className="text-xs font-medium text-gray-500">{taskListTodayLabel}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-black tabular-nums text-primary-600 dark:text-primary-400">
            {loading ? '—' : `${taskListTodayCompletion.pct}%`}
          </span>
          <span className="text-xs text-gray-500">
            {loading
              ? ''
              : t('employerDashboard.completedFraction', {
                  done: taskListTodayCompletion.completedToday,
                  total: taskListTodayCompletion.totalToday,
                })}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
          <div
            className="h-full rounded-full bg-primary-600 transition-all dark:bg-primary-500"
            style={{ width: `${taskListTodayCompletion.pct}%` }}
          />
        </div>
      </section>

      <div className="mb-3 grid grid-cols-2 gap-3">
        <EmployerTaskSummaryTile
          ariaLabel={t('task.list.pendingReviewNavAria', { count: pendingReviewTotal })}
          label={t('task.list.summaryPendingLabel')}
          count={pendingReviewTotal}
          countUnitLabel={summaryCountUnitLabel(pendingReviewTotal)}
          iconWrapClassName="bg-amber-100 text-amber-600 dark:bg-amber-950/55 dark:text-amber-300"
          icon={
            <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          }
          onClick={handlePendingReviewClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handlePendingReviewClick();
            }
          }}
        />
        <EmployerTaskSummaryTile
          ariaLabel={t('task.list.tomorrowNavAria', { count: tomorrowTaskCount })}
          label={t('task.list.summaryTomorrowLabel')}
          count={tomorrowTaskCount}
          countUnitLabel={summaryCountUnitLabel(tomorrowTaskCount)}
          iconWrapClassName="bg-primary-100 text-primary-700"
          icon={
            <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
          onClick={handleTomorrowCardClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleTomorrowCardClick();
            }
          }}
        />
      </div>

      {!loading ? (
        <EmployerOverdueTasksCard
          count={overdueCount}
          title={t('task.list.overdueCardLabel')}
          countUnitLabel={summaryCountUnitLabel(overdueCount)}
          viewLabel={t('task.list.overdueCardView')}
          viewAriaLabel={t('task.list.overdueCardView')}
          onView={() => navigate('/tasks/overdue')}
        />
      ) : null}

      <div
        id="employer-task-date-strip-anchor"
        ref={dateStripAnchorRef}
        className="mb-4 scroll-mt-28"
      >
        {isDateStripPinned && spacerHeightPx > 0 ? (
          <div
            className="pointer-events-none"
            style={{ height: spacerHeightPx }}
            aria-hidden
          />
        ) : null}
        <div
          ref={dateStripPanelRef}
          data-employer-date-strip-panel
          className={
            isDateStripPinned
              ? 'fixed left-1/2 isolate z-[15] w-full max-w-lg -translate-x-1/2 border-b border-gray-200/80 bg-[#f7f8fb] px-4 py-2 shadow-sm dark:border-gray-700 dark:bg-gray-950'
              : undefined
          }
          style={isDateStripPinned ? { top: pinnedTop } : undefined}
        >
        <div
          id="employer-task-date-strip-scroll"
          className="flex gap-2 overflow-x-auto pb-2"
          role="tablist"
          aria-label={t('task.list.dateStripAria')}
        >
          <button
            type="button"
            role="tab"
            aria-selected={isCustomSlotActive}
            tabIndex={0}
            onClick={handleOpenCustomDateModal}
            className={`flex shrink-0 flex-col items-center rounded-xl border px-3 py-2.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              isCustomSlotActive
                ? 'border-primary-600 bg-primary-600 text-white shadow-md'
                : 'border-gray-200/80 bg-white text-gray-500 dark:border-gray-700 dark:bg-gray-900'
            }`}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider">{t('task.list.dateStripCustom')}</span>
            <span className="text-lg font-black leading-tight">···</span>
          </button>
          {stripDays.map((dateYmd) => {
            const isSelected = isSelectedDateOnStrip && selectedDateYmd === dateYmd;
            const isToday = dateYmd === todayYmd;
            return (
              <button
                key={dateYmd}
                type="button"
                role="tab"
                data-date={dateYmd}
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => handleStripDateSelect(dateYmd)}
                className={`flex shrink-0 flex-col items-center rounded-xl border px-3 py-2.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  isSelected
                    ? 'border-primary-600 bg-primary-600 text-white shadow-md'
                    : isToday
                      ? 'border-primary-300 bg-primary-50 text-primary-800 dark:border-primary-700 dark:bg-primary-950/40 dark:text-primary-100'
                      : 'border-gray-200/80 bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'
                }`}
              >
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider ${
                    isSelected
                      ? 'text-white/90'
                      : isToday
                        ? 'text-primary-700 dark:text-primary-200'
                        : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {formatEmployerDateStripWeekday(dateYmd, language)}
                </span>
                <span
                  className={`text-lg font-black leading-tight ${
                    isSelected ? 'text-white' : isToday ? 'text-primary-800 dark:text-primary-50' : ''
                  }`}
                >
                  {formatEmployerDateStripDayNumber(dateYmd)}
                </span>
              </button>
            );
          })}
        </div>
        <h2 className="mt-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          {formatEmployerSelectedDateHeading(selectedDateYmd, language)}
        </h2>
        </div>
      </div>

      <main className={SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}>
        {error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center">
            <p className="text-red-600" role="alert">{error}</p>
          </div>
        ) : null}
        {dayLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : dayError ? (
          <div className="rounded-xl bg-red-50 p-6 text-center">
            <p className="text-red-600" role="alert">{dayError}</p>
          </div>
        ) : dayTasks.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
            <p className="text-gray-600 dark:text-gray-300">{t('task.list.empty')}</p>
            <p className="mt-1 text-sm text-gray-400">{t('task.list.emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedDayTasks.map((group) => (
              <section key={group.bucket} aria-labelledby={`employer-day-bucket-${group.bucket}`}>
                <h3
                  id={`employer-day-bucket-${group.bucket}`}
                  className="mb-3 flex items-center gap-2.5"
                >
                  <EmployerTimeBucketIcon bucket={group.bucket} />
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {t(EMPLOYER_TIME_BUCKET_I18N[group.bucket])}
                  </span>
                </h3>
                <ul className="space-y-2.5" role="list">
                  {group.tasks.map((task) => (
                    <EmployerTaskDayRow
                      key={task.taskInstanceId}
                      task={task}
                      todayYmd={todayYmd}
                      currentMinutes={employerTodayCurrentMinutes}
                      t={t}
                      onOpenDetail={handleOpenDetail}
                      onEditTemplate={(task) =>
                        handleEditTaskInstanceTemplate(task.taskId, task.taskKind)
                      }
                      onReview={handleReview}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>

      <PagePrimaryFab onClick={handleAddTaskFabClick} ariaLabel={t('task.list.addNew')}>
        <span className="text-lg font-bold leading-none" aria-hidden>+</span>
        <span>{t('task.list.addNew')}</span>
      </PagePrimaryFab>

      <AppModal
        open={customDateModalOpen}
        onClose={() => setCustomDateModalOpen(false)}
        title={t('task.list.customDateModalTitle')}
        closeAriaLabel={t('common.cancel')}
        backdropZClass="z-[70]"
        panelMaxWidthClass="max-w-sm"
      >
        <div className="mt-1">
          <IsoDatePicker
            id="employer-task-custom-date"
            ariaLabel={t('task.list.customDateModalTitle')}
            value={customDraftDate}
            onChange={setCustomDraftDate}
            maxDate={maxCustomDateYmd}
            valueFormat="iso"
          />
        </div>
        <div className="mt-5 flex gap-3">
          <AppButton
            type="button"
            variant="secondary"
            onClick={() => setCustomDateModalOpen(false)}
            className="flex-1"
          >
            {t('common.cancel')}
          </AppButton>
          <AppButton type="button" variant="primary" onClick={handleConfirmCustomDate} className="flex-1">
            {t('common.confirm')}
          </AppButton>
        </div>
      </AppModal>

      <AppModal
        open={addTaskChoiceOpen}
        onClose={() => setAddTaskChoiceOpen(false)}
        title={t('task.list.addChoiceTitle')}
        closeAriaLabel={t('common.cancel')}
        backdropZClass="z-[70]"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('task.list.addChoiceSubtitle')}</p>
        <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => handleCreateTaskChoice('/tasks/new', false)}
                className="min-h-12 w-full rounded-xl border-2 border-primary-500 bg-primary-50 py-3 text-base font-semibold text-primary-800 hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-primary-500 dark:bg-primary-950/40 dark:text-primary-100 dark:hover:bg-primary-900/50"
              >
                {t('task.list.addChoiceStandard')}
              </button>
              <button
                type="button"
                onClick={() => handleCreateTaskChoice('/tasks/new/inventory', true)}
                tabIndex={0}
                aria-label={
                  isHighTierTaskKindLocked
                    ? t('task.list.addChoiceLockedAria', { label: t('task.list.addChoiceInventory') })
                    : t('task.list.addChoiceInventory')
                }
                className="min-h-12 w-full rounded-xl border-2 border-orange-500 bg-orange-50 py-3 text-base font-semibold text-orange-900 hover:bg-orange-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:border-orange-600 dark:bg-orange-950/40 dark:text-orange-100 dark:hover:bg-orange-900/50"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {t('task.list.addChoiceInventory')}
                  {isHighTierTaskKindLocked ? <TaskKindLockIcon /> : null}
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleCreateTaskChoice('/tasks/new/pickup-dropoff', true)}
                tabIndex={0}
                aria-label={
                  isHighTierTaskKindLocked
                    ? t('task.list.addChoiceLockedAria', { label: t('task.list.addChoicePickupDropoff') })
                    : t('task.list.addChoicePickupDropoff')
                }
                className="min-h-12 w-full rounded-xl border-2 border-sky-600 bg-sky-50 py-3 text-base font-semibold text-sky-900 hover:bg-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-sky-500 dark:bg-sky-950/40 dark:text-sky-100 dark:hover:bg-sky-900/50"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {t('task.list.addChoicePickupDropoff')}
                  {isHighTierTaskKindLocked ? <TaskKindLockIcon /> : null}
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleCreateTaskChoice('/tasks/new/medication-reminder', true)}
                tabIndex={0}
                aria-label={
                  isHighTierTaskKindLocked
                    ? t('task.list.addChoiceLockedAria', { label: t('task.list.addChoiceMedication') })
                    : t('task.list.addChoiceMedication')
                }
                className="min-h-12 w-full rounded-xl border-2 border-violet-600 bg-violet-50 py-3 text-base font-semibold text-violet-900 hover:bg-violet-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:border-violet-500 dark:bg-violet-950/40 dark:text-violet-100 dark:hover:bg-violet-900/50"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {t('task.list.addChoiceMedication')}
                  {isHighTierTaskKindLocked ? <TaskKindLockIcon /> : null}
                </span>
              </button>
        </div>
      </AppModal>
    </div>
  );
};
